// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A PÁGINA «FORMAÇÃO» — as ações a testar, uma coluna por planeador
// ============================================================================
//
// 2026-10-28 · Spec 2026-10-27 §7 (tarefa 4). Uma página para os planeadores da GVA marcarem o
//    que testaram na cópia de ensaio: ✅ correu bem · ⚠️ correu mal ou teve dúvida (com o
//    porquê). Cada um marca a SUA coluna e vê as dos colegas; o «▸ como» abre o passo-a-passo
//    no guia da sessão.
//
// ⚠️ À PARTE DO MAPA, e de propósito: abre-se em `/formacao` (ramo do `App.tsx` depois da
//    entrada), não carrega os dados do plano e nenhum menu do mapa leva aqui. Removível no fim
//    da formação sem tocar no planeamento — o guarda do `Formacao.test.tsx` recusa que um
//    ficheiro de `mapa/` importe isto.
//
// ⚠️ As marcas de ações que já não existem em `ACOES` (uma ação renomeada) ignoram-se: não
//    contam no progresso nem aparecem em linha nenhuma.
//
// 📗 2026-10-28 · pedidos do cliente:
//    · **quem participa é uma lista explícita** (migração 103), editada aqui no botão «Quem
//      participa» — só o superadmin ou um proprietário de qualquer plano o vê (o servidor diz
//      `podeEditar`, e recusa com 403 aos outros). As colunas são os participantes; quem já marcou
//      e saiu da lista fica numa coluna à parte, «já não participa», para nenhuma marca
//      desaparecer. Já não dependem do plano activo da sessão;
//    · **cada sessão liga à sua apresentação**, num separador novo do navegador — e, só a quem
//      gere a lista, ao **plano do formador** (o guião da demo), servido pela API e não por
//      `public/`;
// 📗 2026-10-30 · Cliente: *«tudo pode ser editado, os dados não são reais e estão
//    desactualizados, eles podem partir tudo se quiserem»* — saiu o aviso «isto muda também o plano
//    real — teste com uma alteração pequena e desfaça com Ctrl+Z» das linhas das ações.
//    · **só os participantes marcam** — o servidor recusa os outros (403), e a página diz a quem
//      pedir para entrar na lista.
//
// 📗 2026-10-29 · «GUIA E AVALIAÇÃO JUNTOS» (decisão do cliente): marca-se no FIM DE CADA CARTÃO DO
//    GUIA (`apps/web/public/formacao/guias/formacao-guia.js`, a mesma API), e esta página passa a
//    RESUMO — o progresso de cada pessoa, a grelha de todos (só leitura, com os comentários
//    inteiros), as ligações do guia, da apresentação e do plano do formador de cada sessão, e
//    «Quem participa». ⚠️ Saiu daqui a lista para marcar ação a ação: com a marca no guia ficava
//    DUPLICADA — dois sítios para o mesmo gesto, e o que divergisse (a regra do rascunho, a frase
//    da recusa) era o que ninguém conferia. A grelha de leitura é o que o Leonel precisa para ver
//    quem marcou o quê; responder a um comentário faz-se na sessão seguinte (não há onde gravar
//    uma resposta, e criá-lo era uma funcionalidade nova).
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { escreverNaRede, fraseDaLeituraFalhada, lerDaRede } from '../mapa/rede.js';
import { ACOES, SESSOES, type AcaoDaFormacao } from './acoes.js';
import './formacao.css';

type Estado = 'ok' | 'duvida';

interface Pessoa {
  readonly id: string;
  readonly nome: string;
}

/** Uma coluna: a pessoa e se está na lista de quem participa (`false` = «já não participa»). */
interface Coluna extends Pessoa {
  readonly participa: boolean;
}

interface Marca {
  readonly acaoId: string;
  readonly utilizadorId: string;
  readonly estado: Estado;
  readonly comentario: string | null;
  readonly em: string;
}

interface RespostaFormacao {
  readonly activa: true;
  readonly pessoas: readonly Coluna[];
  readonly marcas: readonly Marca[];
  /** Pode editar a lista de quem participa (superadmin ou proprietário de algum plano)? */
  readonly podeEditar: boolean;
  /** Só a quem pode editar: as contas que se podem escolher. */
  readonly candidatos?: readonly Pessoa[];
}

interface RespostaSessao {
  readonly utilizador: { readonly id: string; readonly nome: string } | null;
}

type Leitura =
  | { readonly tipo: 'a_carregar' }
  | { readonly tipo: 'desligada' }
  | { readonly tipo: 'erro'; readonly frase: string }
  | {
      readonly tipo: 'pronto';
      readonly pessoas: readonly Coluna[];
      readonly marcas: readonly Marca[];
      readonly podeEditar: boolean;
      readonly candidatos: readonly Pessoa[];
    };

/** A resposta da API como o ecrã a guarda. */
function prontoDe(c: RespostaFormacao): Leitura {
  return {
    tipo: 'pronto',
    pessoas: c.pessoas,
    marcas: c.marcas,
    podeEditar: c.podeEditar === true,
    candidatos: c.candidatos ?? [],
  };
}

const IDS_DAS_ACOES = new Set(ACOES.map((a) => a.id));

/** `2026-11-17` → «terça 17/11». */
function dataCurta(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const dia = d.toLocaleDateString('pt-PT', { weekday: 'long' }).replace('-feira', '');
  return `${dia} ${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

const chave = (acaoId: string, utilizadorId: string) => `${acaoId}\u0000${utilizadorId}`;

export function Formacao(): React.ReactElement {
  const [leitura, definirLeitura] = useState<Leitura>({ tipo: 'a_carregar' });
  const [eu, definirEu] = useState<Pessoa | null>(null);
  const [sessao, definirSessao] = useState<number>(SESSOES[0]?.n ?? 1);
  /** O painel «Quem participa» está aberto? */
  const [aEditarLista, definirAEditarLista] = useState(false);

  useEffect(() => {
    const abortar = new AbortController();
    void (async () => {
      const [rs, rf] = await Promise.all([
        lerDaRede<RespostaSessao>('/api/auth/sessao', abortar.signal),
        lerDaRede<RespostaFormacao>('/api/formacao', abortar.signal),
      ]);
      if (rs.tipo === 'ok' && rs.corpo.utilizador !== null) {
        definirEu({ id: rs.corpo.utilizador.id, nome: rs.corpo.utilizador.nome });
      }
      if (rf.tipo === 'abortada') return;
      if (rf.tipo === 'ok') {
        definirLeitura(prontoDe(rf.corpo));
      } else if (rf.tipo === 'erro' && rf.estado === 404) {
        definirLeitura({ tipo: 'desligada' });
      } else if (rf.tipo === 'erro' && rf.estado === 401) {
        definirLeitura({
          tipo: 'erro',
          frase: 'A sessão terminou. Recarregue a página para entrar outra vez.',
        });
      } else {
        definirLeitura({
          tipo: 'erro',
          // 2026-10-28 · nunca uma caixa de erro vazia: o `null` só vem de «ok»/«abortada», que
          //    não chegam aqui — mas se o tipo de `rf` crescer, diz-se alguma coisa em vez de nada.
          frase:
            fraseDaLeituraFalhada(rf, 'a página da formação') ??
            'Não foi possível ler a página da formação. Recarregue a página para tentar outra vez.',
        });
      }
    })();
    return () => abortar.abort();
  }, []);

  /**
   * As colunas, tal como o servidor as manda: os participantes primeiro e, no fim, quem já
   * marcou e saiu da lista. 📌 2026-10-28 · o próprio já NÃO é acrescentado à força: só tem
   * coluna onde marcar se estiver na lista (pedido do cliente — há colaboradores que não fazem a
   * formação).
   */
  const pessoas = useMemo<readonly Coluna[]>(
    () => (leitura.tipo === 'pronto' ? leitura.pessoas : []),
    [leitura],
  );
  /** O próprio está na lista de quem participa — logo pode marcar. */
  const euParticipo = eu !== null && pessoas.some((p) => p.id === eu.id && p.participa);

  const marcas = useMemo(() => {
    const m = new Map<string, Marca>();
    if (leitura.tipo === 'pronto') {
      for (const x of leitura.marcas) {
        if (IDS_DAS_ACOES.has(x.acaoId)) m.set(chave(x.acaoId, x.utilizadorId), x);
      }
    }
    return m;
  }, [leitura]);

  /** Grava a lista de quem participa e relê a página (as colunas mudam). */
  const gravarLista = useCallback(async (ids: readonly string[]): Promise<string | null> => {
    const r = await escreverNaRede<{ participantes: string[] }>(
      '/api/formacao/participantes',
      { utilizadores: ids },
      undefined,
      'PUT',
    );
    if (r.tipo === 'recusa') return `Não ficou gravado: ${r.frase}`;
    const rf = await lerDaRede<RespostaFormacao>('/api/formacao');
    if (rf.tipo !== 'ok') {
      return (
        'A lista ficou gravada, mas não foi possível reler a página — recarregue-a. ' +
        (fraseDaLeituraFalhada(rf, 'a página da formação') ?? '')
      );
    }
    definirLeitura(prontoDe(rf.corpo));
    definirAEditarLista(false);
    return null;
  }, []);

  const cabecalho = (
    <header className="formacao-topo">
      <img className="formacao-logo" src="/logo-xlvision.png" alt="xlVISION" />
      <h1 className="formacao-titulo">MPT · Formação e testes</h1>
      {eu !== null ? <span className="formacao-eu">Eu: <strong>{eu.nome}</strong></span> : null}
      <a className="formacao-voltar" href="/">Abrir o mapa</a>
    </header>
  );

  if (leitura.tipo !== 'pronto') {
    return (
      <div className="formacao-pagina">
        {cabecalho}
        <p className="formacao-estado" role={leitura.tipo === 'erro' ? 'alert' : undefined}>
          {leitura.tipo === 'a_carregar'
            ? 'A carregar…'
            : leitura.tipo === 'desligada'
              ? 'A formação não está ativa neste momento.'
              : leitura.frase}
        </p>
      </div>
    );
  }

  // 📗 2026-10-29 · O progresso é POR PESSOA, sobre o número de ações da lista. Antes era um total
  //    da equipa — «0 de 244» —, que contava ações × participantes: para um planeador «244» não
  //    quer dizer nada, e o número mudava quando mudava a lista («1 de 122» quando alguém saiu).
  //    Escolhido em vez de «ações testadas por todos» porque cada pessoa vê logo quanto lhe falta,
  //    e o denominador é sempre o mesmo. Só as ações que existem (uma marca órfã não conta) e só
  //    quem PARTICIPA — as marcas de quem saiu ficam à vista na coluna dele, mas não aqui.
  const participantes = new Set(pessoas.filter((p) => p.participa).map((p) => p.id));
  const progresso = pessoas
    .filter((p) => p.participa)
    .map((p) => {
      let bem = 0;
      let duvida = 0;
      for (const a of ACOES) {
        const m = marcas.get(chave(a.id, p.id));
        if (m?.estado === 'ok') bem += 1;
        else if (m?.estado === 'duvida') duvida += 1;
      }
      return { p, bem, duvida };
    });

  const dados = SESSOES.find((s) => s.n === sessao);
  const acoes = ACOES.filter((a) => a.sessao === sessao);

  return (
    <div className="formacao-pagina">
      {cabecalho}
      {progresso.length > 0 ? (
        <p className="formacao-progresso" data-testid="formacao-progresso">
          Progresso de cada pessoa:{' '}
          {progresso.map(({ p, bem, duvida }, i) => (
            <span key={p.id}>
              {i > 0 ? ' · ' : ''}
              {p.nome}: <strong>{bem + duvida} de {ACOES.length}</strong> ({bem} ✅ · {duvida} ⚠️)
            </span>
          ))}
        </p>
      ) : null}
      <p className="formacao-ajuda">
        Marca-se no fim de cada cartão do guia da sessão: ✅ correu bem · ⚠️ correu mal ou teve
        dúvida (com o porquê). Aqui fica o resumo de todos; o «▸ como» abre o cartão da ação.
      </p>
      {participantes.size === 0 ? (
        <p className="formacao-nota">
          Ainda ninguém está na lista de quem participa.{' '}
          {leitura.podeEditar
            ? 'Use «Quem participa» para escolher as pessoas.'
            : 'Peça ao Leonel (ou a um proprietário de um plano) para o pôr na lista.'}
        </p>
      ) : eu !== null && !euParticipo && leitura.podeEditar ? (
        // 📗 2026-10-29 · quem GERE a lista e não faz a formação (o Leonel) via um aviso sempre que
        //    abria a página — ruído permanente. Passa a uma linha discreta; se quiser marcar, o
        //    botão «Quem participa», logo abaixo, é o caminho (o servidor recusa a marca a quem não
        //    está na lista, quem gere incluído).
        <p className="formacao-gere">Gere a formação; não está na lista de participantes.</p>
      ) : eu !== null && !euParticipo ? (
        <p className="formacao-nota">
          Não está na lista de quem participa: pode ver as marcas, mas não marcar no guia.{' '}
          {/* 📗 2026-10-28 · o servidor recusa a marca a quem não está na lista (403
              `nao_participa_na_formacao`) — diz-se a quem pedir. */}
          Para marcar, peça a quem gere a formação (o Leonel, ou o proprietário de um plano) para o acrescentar.
        </p>
      ) : null}
      {leitura.podeEditar ? (
        <div className="formacao-lista-zona">
          {aEditarLista ? (
            <QuemParticipa
              candidatos={leitura.candidatos}
              escolhidos={pessoas.filter((p) => p.participa).map((p) => p.id)}
              aoGravar={gravarLista}
              aoCancelar={() => definirAEditarLista(false)}
            />
          ) : (
            <button type="button" className="formacao-botao" onClick={() => definirAEditarLista(true)}>
              Quem participa
            </button>
          )}
        </div>
      ) : null}

      <div className="formacao-separadores" role="tablist">
        {SESSOES.map((s) => (
          <button
            key={s.n}
            type="button"
            role="tab"
            aria-selected={s.n === sessao}
            className={s.n === sessao ? 'formacao-separador formacao-separador--activo' : 'formacao-separador'}
            onClick={() => definirSessao(s.n)}
          >
            {s.n} · {dataCurta(s.data)}
          </button>
        ))}
      </div>

      {dados !== undefined ? (
        <h2 className="formacao-sessao">
          Sessão {dados.n} · {dataCurta(dados.data)} · {acoes.length} ações
          <span className="formacao-tema">{dados.tema}</span>
        </h2>
      ) : null}
      {dados !== undefined ? (
        <p className="formacao-apresentacao">
          {/* 📗 2026-10-29 · o guia da sessão é onde se marca — a primeira ligação. */}
          <a
            className="formacao-apresentacao-ligacao"
            href={`/formacao/guias/sessao-${String(dados.n)}.html`}
            target="_blank"
            rel="noopener"
          >
            Guia da sessão {dados.n}
          </a>
          {' · '}
          <a
            className="formacao-apresentacao-ligacao"
            href={`/formacao/apresentacoes/sessao-${String(dados.n)}.html`}
            target="_blank"
            rel="noopener"
          >
            Apresentação da sessão {dados.n}
          </a>
          {/* 📗 2026-10-28 · o guião da demo é uma peça à parte, só para quem gere a formação: a
              rota da API recusa (403) os outros, e aqui a ligação nem aparece. */}
          {leitura.podeEditar ? (
            <>
              {' · '}
              <a
                className="formacao-apresentacao-ligacao"
                href={`/api/formacao/plano-do-formador/${String(dados.n)}`}
                target="_blank"
                rel="noopener"
              >
                Plano do formador — sessão {dados.n}
              </a>
            </>
          ) : null}
        </p>
      ) : null}

      <div className="formacao-rolar">
        <table className="formacao-tabela">
          <thead>
            <tr>
              <th scope="col">Ação a testar</th>
              {pessoas.map((p) => (
                <th
                  key={p.id}
                  scope="col"
                  className={
                    !p.participa ? 'formacao-coluna-saiu' : p.id === eu?.id ? 'formacao-coluna-eu' : undefined
                  }
                >
                  {p.nome}
                  {p.id === eu?.id ? ' (eu)' : ''}
                  {!p.participa ? <span className="formacao-saiu">já não participa</span> : null}
                </th>
              ))}
              <th scope="col">Comentários</th>
            </tr>
          </thead>
          <tbody>
            {acoes.map((a) => (
              <LinhaDaAcao key={a.id} acao={a} pessoas={pessoas} eu={eu} marcas={marcas} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * O painel «Quem participa»: todas as contas activas, com caixas de marcar. Grava a lista
 * inteira (o que está marcado) por `PUT /api/formacao/participantes`.
 *
 * ⚠️ Tirar alguém da lista NÃO apaga as marcas dele: a coluna passa a «já não participa» —
 *    di-lo o próprio painel, para ninguém hesitar em tirar.
 */
function QuemParticipa({
  candidatos,
  escolhidos,
  aoGravar,
  aoCancelar,
}: {
  readonly candidatos: readonly Pessoa[];
  readonly escolhidos: readonly string[];
  readonly aoGravar: (ids: readonly string[]) => Promise<string | null>;
  readonly aoCancelar: () => void;
}) {
  const [marcados, definirMarcados] = useState<ReadonlySet<string>>(() => new Set(escolhidos));
  const [aGravar, definirAGravar] = useState(false);
  const [falha, definirFalha] = useState<string | null>(null);

  const gravar = async () => {
    definirAGravar(true);
    definirFalha(null);
    const frase = await aoGravar([...marcados]);
    definirAGravar(false);
    if (frase !== null) definirFalha(frase);
  };

  return (
    <fieldset className="formacao-lista">
      <legend className="formacao-lista-titulo">Quem participa na formação</legend>
      <p className="formacao-ajuda">
        Marque as pessoas que fazem a formação: cada uma fica com uma coluna. Tirar alguém da
        lista não apaga as marcas que já fez — a coluna fica à parte, como «já não participa».
      </p>
      <ul className="formacao-lista-pessoas">
        {candidatos.map((c) => (
          <li key={c.id}>
            <label className="formacao-lista-pessoa">
              <input
                type="checkbox"
                checked={marcados.has(c.id)}
                disabled={aGravar}
                onChange={(e) =>
                  definirMarcados((m) => {
                    const n = new Set(m);
                    if (e.target.checked) n.add(c.id);
                    else n.delete(c.id);
                    return n;
                  })
                }
              />{' '}
              {c.nome}
            </label>
          </li>
        ))}
      </ul>
      {falha !== null ? (
        <p className="formacao-falha" role="alert">{falha}</p>
      ) : null}
      <button type="button" className="formacao-botao" disabled={aGravar} onClick={() => void gravar()}>
        {aGravar ? 'A gravar…' : 'Gravar a lista'}
      </button>{' '}
      <button type="button" className="formacao-botao" disabled={aGravar} onClick={aoCancelar}>
        Cancelar
      </button>
    </fieldset>
  );
}

/**
 * Uma linha da grelha, SÓ DE LEITURA (📗 2026-10-29): a ação, a marca de cada pessoa e os
 * comentários de todos, inteiros — o do próprio também. Marca-se no guia.
 */
function LinhaDaAcao({
  acao,
  pessoas,
  eu,
  marcas,
}: {
  readonly acao: AcaoDaFormacao;
  readonly pessoas: readonly Pessoa[];
  readonly eu: Pessoa | null;
  readonly marcas: ReadonlyMap<string, Marca>;
}) {
  const comentarios = pessoas
    .map((p) => ({ p, m: marcas.get(chave(acao.id, p.id)) }))
    .filter((x) => x.m?.comentario != null);

  return (
    <tr>
      <td className="formacao-acao">
        <span className="formacao-acao-titulo">{acao.titulo}</span>{' '}
        <a
          className="formacao-como"
          href={`/formacao/guias/sessao-${String(acao.sessao)}.html#${acao.comoAncora}`}
          target="_blank"
          rel="noopener"
        >
          ▸ como
        </a>
        {acao.ondeTestar === 'demo' ? (
          <span className="formacao-demo">vê-se na demonstração</span>
        ) : null}
      </td>
      {pessoas.map((p) => {
        const m = marcas.get(chave(acao.id, p.id));
        const rotulo = m === undefined ? 'por marcar' : m.estado === 'ok' ? 'correu bem' : 'teve dúvida';
        return (
          <td
            key={p.id}
            className={p.id === eu?.id ? 'formacao-celula formacao-coluna-eu' : 'formacao-celula'}
            aria-label={`${p.nome}: ${rotulo}`}
          >
            {m === undefined ? '☐' : m.estado === 'ok' ? '✅' : '⚠️'}
          </td>
        );
      })}
      <td className="formacao-comentarios">
        {comentarios.map(({ p, m }) => (
          <p key={p.id} className="formacao-comentario">
            <span className="formacao-comentario-nome">{p.id === eu?.id ? `${p.nome} (eu)` : p.nome}:</span>{' '}
            {m!.comentario}
          </p>
        ))}
      </td>
    </tr>
  );
}
